import axios, { AxiosError, AxiosInstance } from 'axios';
import createAuthRefreshInterceptor from 'axios-auth-refresh';
import { ApiError, FieldsError, ForbiddenError } from './Error';
import { sentry } from '@/services/sentry';
import { ProfileType } from '@/modules/profile/types';
import { PreferencesType } from '@/modules/preferences/types';
import {
  mapAiDetectorResponse,
  mapAiHumanizerResponse,
  mapMetaResponse,
  mapParaphraserResponse,
  mapPlagiarismCheckerResponse,
  mapPreferencesResponse,
  mapProfileResponse,
  mapProjectResponse,
  mapProjectSettingsResponse,
} from '@/api/mappers';

import {
  ProjectFiltersType,
  ProjectListResponseType,
  ProjectSettingsType,
  ProjectType,
} from '@/modules/projects/types';
import { PlagiarismCheckerResponseType } from '@/modules/tools/PlagiarismChecker/types';
import { DetectionStatusType, ToolReactionPayload } from '@/modules/tools/types';
import { AiDetectorResponseType } from '@/modules/tools/AiDetector/types';
import { ParaphraserResponseType } from '@/modules/tools/Paraphraser/types';
import { AiHumanizerResponseType } from '@/modules/tools/AiHumanizer/types';

export const BASE_API_URL = process.env.NEXT_PUBLIC_API_URL || '/';

export class PlatformApi {
  private axios: AxiosInstance;
  private notAuthorizedCallback?: () => void;

  constructor() {
    this.axios = axios.create({
      baseURL: BASE_API_URL,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    createAuthRefreshInterceptor(this.axios, this.refreshAuthLogic, {
      statusCodes: [401],
      pauseInstanceWhileRefreshing: true,
    });

    this.axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error instanceof ApiError) {
          return Promise.reject(error);
        }

        const status = error.response?.status;
        const config = error.config;

        if (status === 401) {
          return Promise.reject(error);
        }

        if (status === 403) {
          this.notAuthorizedCallback?.();
        }

        const handledError = await PlatformApi.handleError(error, config?.url || 'Unknown');
        return Promise.reject(handledError);
      },
    );
  }

  private refreshAuthLogic = async (failedRequest: AxiosError): Promise<void> => {
    try {
      // @ts-ignore
      const responseCode = failedRequest.response?.data?.code || '';
      const config = failedRequest.config;

      if (responseCode === 'auth_middleware::invalid_token') {
        await this.axios.post('/api/v1/auth/refresh-token', {});
        return Promise.resolve();
      }

      this.notAuthorizedCallback?.();
      const handledError = await PlatformApi.handleError(failedRequest, config?.url || 'Unknown');
      return Promise.reject(handledError);
      // throw failedRequest;
    } catch (error: any) {
      const config = error?.config;

      this.notAuthorizedCallback?.();
      const handledError = await PlatformApi.handleError(error, config?.url || 'Unknown');
      return Promise.reject(handledError);
    }
  };

  public setNotAuthorizedCallback(callback?: () => void) {
    this.notAuthorizedCallback = callback;
  }

  static async handleError(e: any, methodName: string): Promise<Error> {
    const status = e?.response?.status;
    const responseData = e?.response?.data;
    const responseCode = responseData?.code;
    const message = responseData?.message || e.message;

    if (status === 403) {
      return new ForbiddenError(message || 'Forbidden', responseCode);
    }

    if (responseData?.fields && (status === 400 || status === 422)) {
      return new FieldsError({ ...responseData, message: message || 'Validation failed', code: responseCode });
    }

    if (status === 429) {
      const err = new Error(`429 Too Many Requests in ${methodName}`);

      sentry.captureException(err, {
        status,
        method: e?.config?.method,
        url: e?.config?.url,
        code: responseCode,
      });

      return new ApiError('Too many requests. Please wait...', 429, responseCode);
    }

    if (!status) {
      const err = new Error(`Network/Unknown error in ${methodName}`);

      sentry.captureException(err, {
        status: 0,
        url: e?.config?.url,
        method: e?.config?.method,
        code: responseCode,
      });

      return new ApiError(message || 'Network error', 0, responseCode);
    }

    if (status >= 500) {
      const err = new Error(`Server Error in ${methodName}: ${status}`);
      sentry.captureException(err, {
        status,
        url: e?.config?.url,
        method: e?.config?.method,
        code: responseCode,
      });
      return new ApiError('Server error', status || 500, responseCode);
    }

    return new ApiError(message || `Error in ${methodName}`, status || 0, responseCode);
  }

  async signIn(email: string, password: string): Promise<ProfileType> {
    const { data } = await this.axios.post('/api/v1/auth/local/login', {
      email: email.trim(),
      password,
    });

    if (data?.data?.user) {
      return mapProfileResponse(data.data.user);
    }

    throw new Error('Invalid sign-in response');
  }

  async signUp(payload: { email: string; password: string }): Promise<ProfileType> {
    const { data } = await this.axios.post('/api/v1/auth/local/sign-up', {
      ...payload,
      email: payload.email.trim(),
    });

    if (data?.data?.user) {
      return mapProfileResponse(data.data.user);
    }

    throw new Error('Invalid sign-up response');
  }

  async signOut(): Promise<void> {
    await this.axios.post('/api/v1/auth/logout');
  }

  async forgotPassword(email: string): Promise<void> {
    await this.axios.post('/api/v1/auth/local/password-forgot', { email: email.trim() });
  }

  async resetPassword(token: string, new_password: string): Promise<void> {
    await this.axios.post('/api/v1/auth/local/password-reset', { token, new_password });
  }

  async changePassword(old_password: string, new_password: string): Promise<void> {
    await this.axios.put('/api/v1/password', { old_password, new_password });
    await Promise.resolve();
  }

  async getProfile(): Promise<ProfileType> {
    const { data } = await this.axios.get('/api/v1/profile');

    if (data?.data?.user) {
      return mapProfileResponse(data.data.user);
    }

    throw new Error('Invalid response');
  }

  async updateProfile(payload: ProfileType): Promise<ProfileType> {
    const { data } = await this.axios.put('/api/v1/profile', payload);
    if (data?.data?.user) {
      return mapProfileResponse(data.data.user);
    }

    throw new Error('Invalid response');
  }

  async getPreferences(): Promise<PreferencesType> {
    const { data } = await this.axios.get('/api/v1/preferences');

    if (data?.data) {
      return mapPreferencesResponse(data.data.preferences || {});
    }

    throw new Error('Invalid response');
  }

  async updatePreferences(payload: PreferencesType): Promise<PreferencesType> {
    const { data } = await this.axios.put('/api/v1/preferences', payload);
    if (data?.data?.preferences) {
      return mapPreferencesResponse(data.data.preferences);
    }

    throw new Error('Invalid response');
  }

  async deleteAccount(): Promise<void> {
    // TODO! Think about using generated-api instead of magic string
    await this.axios.delete('/api/v1/profile');
  }

  async createProject(payload?: Partial<ProjectType>): Promise<ProjectType> {
    const { data } = await this.axios.post('/api/v1/projects', payload);

    if (data.data) {
      return mapProjectResponse(data.data.project || {});
    }

    throw new Error('Invalid response');
  }

  async getProjectList(filters: ProjectFiltersType = {}): Promise<ProjectListResponseType> {
    const { data } = await this.axios.get('/api/v1/projects', {
      params: filters,
    });

    if (data?.data) {
      return {
        data: (data.data.projects || []).map(mapProjectResponse),
        meta: mapMetaResponse(data.data.meta || {}),
      };
    }

    throw new Error('Invalid response');
  }

  async getProjectById(projectId: string): Promise<ProjectType | null> {
    const { data } = await this.axios.get(`/api/v1/projects/${projectId}`);

    if (data?.data) {
      return mapProjectResponse(data.data.project || {});
    }

    throw new Error('Invalid response');
  }

  async updateProjectById(projectId: string, payload: ProjectType): Promise<ProjectType> {
    const { data } = await this.axios.put(`/api/v1/projects/${projectId}`, payload);

    if (data?.data) {
      return mapProjectResponse(data.data.project || {});
    }

    throw new Error('Invalid response');
  }

  async deleteProjectById(projectId: string): Promise<void> {
    await this.axios.delete(`/api/v1/projects/${projectId}`);
  }

  async getProjectSettingsById(projectId: string): Promise<ProjectSettingsType> {
    const { data } = await this.axios.get(`/api/v1/projects/${projectId}/settings`);

    if (data?.data) {
      return mapProjectSettingsResponse(data.data.settings || {});
    }

    throw new Error('Invalid response');
  }

  async updateProjectSettingsById(projectId: string, payload: ProjectSettingsType): Promise<ProjectSettingsType> {
    const { data } = await this.axios.put(`/api/v1/projects/${projectId}/settings`, payload);

    if (data?.data) {
      return mapProjectSettingsResponse(data.data.settings || {});
    }

    throw new Error('Invalid response');
  }

  async sendPlagiarismCheckerCheck(
    projectId: string,
    fields: { text: string },
  ): Promise<PlagiarismCheckerResponseType> {
    const { data } = await this.axios.post(`/api/v1/projects/${projectId}/plagiarism-checks`, fields);

    if (data.data) {
      return mapPlagiarismCheckerResponse(data.data.plagiarism_check || {});
    }

    throw new Error('Invalid response');
  }

  async discardPlagiarismCheckerCheck(projectId: string, checkId: string): Promise<void> {
    await this.axios.delete(`/api/v1/projects/${projectId}/plagiarism-checks/${checkId}`);
  }

  async getPlagiarismCheckerListByProjectId(projectId: string): Promise<PlagiarismCheckerResponseType[] | null> {
    // TODO! Add filters and return {data: {}, meta: {}}
    const { data } = await this.axios.get(`/api/v1/projects/${projectId}/plagiarism-checks`, {
      params: {
        page: 1,
        page_size: 10,
      },
    });

    if (data?.data) {
      return (data.data.checks || []).map(mapPlagiarismCheckerResponse);
    }

    throw new Error('Invalid response');
  }

  async updatePlagiarismCheckerItemDetection({
    projectId,
    checkId,
    detectionId,
    status,
  }: {
    projectId: string;
    checkId: string;
    detectionId: string;
    status: DetectionStatusType;
  }) {
    const { data } = await this.axios.put(
      `/api/v1/projects/${projectId}/plagiarism-checks/${checkId}/detections/${detectionId}`,
      { status },
    );

    if (data?.data) {
      return mapPlagiarismCheckerResponse(data.data?.plagiarism_check || {});
    }

    throw new Error('Invalid response');
  }

  async sendAiDetectorCheck(projectId: string, fields: { text: string }): Promise<AiDetectorResponseType> {
    const { data } = await this.axios.post(`/api/v1/projects/${projectId}/ai-detections`, fields);

    if (data.data) {
      return mapAiDetectorResponse(data.data || {});
    }

    throw new Error('Invalid response');
  }

  async discardAiDetectorCheck(projectId: string, checkId: string): Promise<void> {
    await this.axios.delete(`/api/v1/projects/${projectId}/ai-detections/${checkId}`);
  }

  async getAiDetectorListByProjectId(projectId: string): Promise<PlagiarismCheckerResponseType[] | null> {
    // TODO! Add filters and return {data: {}, meta: {}}
    const { data } = await this.axios.get(`/api/v1/projects/${projectId}/ai-detections`, {
      params: {
        page: 1,
        page_size: 10,
      },
    });

    // TODO! We need to get unify response format everywhere
    if (data?.data) {
      return (data.data.data || []).map(mapAiDetectorResponse);
    }

    throw new Error('Invalid response');
  }

  async updateAiDetectorItemDetection({
    projectId,
    checkId,
    detectionId,
    status,
  }: {
    projectId: string;
    checkId: string;
    detectionId: string;
    status: DetectionStatusType;
  }) {
    const { data } = await this.axios.put(
      `/api/v1/projects/${projectId}/ai-detections/${checkId}/detections/${detectionId}`,
      { status },
    );

    if (data?.data) {
      return mapAiDetectorResponse(data.data || {});
    }

    throw new Error('Invalid response');
  }

  async sendParaphraserCheck(projectId: string, original_text: string): Promise<ParaphraserResponseType> {
    const { data } = await this.axios.post(`/api/v1/projects/${projectId}/paraphrases`, { original_text });

    if (data.data) {
      return mapParaphraserResponse(data.data || {});
    }

    throw new Error('Invalid response');
  }

  async sendParaphraserAdjustmentsCheck({
    projectId,
    checkId,
    adjustment,
  }: {
    projectId: string;
    checkId: string;
    adjustment: string;
  }): Promise<ParaphraserResponseType> {
    const { data } = await this.axios.post(`/api/v1/projects/${projectId}/paraphrases/${checkId}/adjustments`, {
      adjustment,
    });

    if (data.data) {
      return mapParaphraserResponse(data.data || {});
    }

    throw new Error('Invalid response');
  }

  async discardParaphraserCheck(projectId: string, checkId: string): Promise<void> {
    await this.axios.delete(`/api/v1/projects/${projectId}/paraphrases/${checkId}`);
  }

  async updateParaphraserItem({
    projectId,
    checkId,
    status,
  }: {
    projectId: string;
    checkId: string;
    status: string;
  }): Promise<ParaphraserResponseType> {
    const { data } = await this.axios.put(`/api/v1/projects/${projectId}/paraphrases/${checkId}`, { status });

    if (data?.data) {
      return mapParaphraserResponse(data.data || {});
    }

    throw new Error('Invalid response');
  }

  async sendAiHumanizerCheck(projectId: string, original_text: string): Promise<AiHumanizerResponseType> {
    const { data } = await this.axios.post(`/api/v1/projects/${projectId}/humanizes`, { original_text });

    if (data.data) {
      return mapAiHumanizerResponse(data.data || {});
    }

    throw new Error('Invalid response');
  }

  async sendAiHumanizerAdjustmentsCheck({
    projectId,
    checkId,
    adjustment,
  }: {
    projectId: string;
    checkId: string;
    adjustment: string;
  }): Promise<AiHumanizerResponseType> {
    const { data } = await this.axios.post(`/api/v1/projects/${projectId}/humanizes/${checkId}/adjustments`, {
      adjustment,
    });

    if (data.data) {
      return mapAiHumanizerResponse(data.data || {});
    }

    throw new Error('Invalid response');
  }

  async discardAiHumanizerCheck(projectId: string, checkId: string): Promise<void> {
    await this.axios.delete(`/api/v1/projects/${projectId}/humanizes/${checkId}`);
  }

  async updateAiHumanizerItem({
    projectId,
    checkId,
    status,
  }: {
    projectId: string;
    checkId: string;
    status: string;
  }): Promise<AiHumanizerResponseType> {
    const { data } = await this.axios.put(`/api/v1/projects/${projectId}/humanizes/${checkId}`, { status });

    if (data?.data) {
      return mapAiHumanizerResponse(data.data || {});
    }

    throw new Error('Invalid response');
  }

  async sendToolReaction({ responseId, tool, reaction }: ToolReactionPayload): Promise<void> {
    await this.axios.post(`/api/v1/reactions`, {
      entity_id: responseId,
      entity_type: tool,
      reaction,
    });
  }
}

const Api = new PlatformApi();

export default Api;
